import * as Yup from 'yup';

import {
  ApiEndpoint,
  FormActionType,
  FormType,
  InputType,
  ServiceModalName,
  Translation,
  WorkspaceMemberEndpoint,
} from '@enums';
import i18n from '@translations';
import type { EndpointType, FormConfig, FormConfigMapping, Options } from '@types';

interface GetWorkspaceFormConfigParams {
  endpoint?: EndpointType;
  options?: {
    [K in string]: Options;
  };
  schema: Yup.AnySchema;
}

export const getWorkspaceFormConfig = ({
  endpoint = ApiEndpoint.DEFAULT,
  options,
  schema,
}: GetWorkspaceFormConfigParams): FormConfig => {
  const tModal: (key: string) => string = (key: string): string => i18n.t(`${Translation.Workspace}:modals.${key}`);

  const tDescription: (key: string) => string = (key: string): string =>
    i18n.t(`${Translation.Workspace}:descriptions.${key}`);

  const tForm: (key: string) => string = (key: string): string => i18n.t(`${Translation.Workspace}:forms.${key}`);

  const tButton: (key: string) => string = (key: string): string => i18n.t(`${Translation.Workspace}:buttons.${key}`);

  const tcButton: (key: string) => string = (key: string): string => i18n.t(`${Translation.Common}:button.${key}`);

  const WORKSPACE_FORM: FormConfigMapping = {
    [ApiEndpoint.DEFAULT]: {
      formType: FormType.Workspace,
      endpoint: ApiEndpoint.CONFIGURATION,
      formActionType: FormActionType.Edit,
      modalName: ServiceModalName.FormModal,
      label: {
        title: tModal('modifyWorkspaceSettings'),
        description: tDescription('updateYourWorkspaceNameAndDetails'),
        confirmButton: tcButton('update'),
      },
      inputList: [
        {
          name: 'name',
          defaultValue: '',
          inputType: InputType.Text,
          required: true,
          placeholder: tForm('chooseAUniqueNameToIdentifyYourWorkspace'),
          label: tForm('workspaceName'),
        },
      ],
      validationSchema: schema,
    },
    [WorkspaceMemberEndpoint.TRANSFER]: {
      formType: FormType.Workspace,
      endpoint: WorkspaceMemberEndpoint.TRANSFER,
      formActionType: FormActionType.Transfer,
      modalName: ServiceModalName.FormModal,
      label: {
        title: tButton('transferOwnership'),
        description: tDescription('transferControlOfThisWorkspaceToAnotherUser'),
        confirmButton: tcButton('transfer'),
      },
      inputList: [
        {
          name: 'email',
          defaultValue: '',
          inputType: InputType.Select,
          required: true,
          placeholder: tForm('selectNewWorkspaceOwner'),
          label: tForm('ownership'),
          options: options?.ownerOptions,
        },
      ],
      validationSchema: schema,
    },
    [WorkspaceMemberEndpoint.MEMBER]: {
      formType: FormType.Workspace,
      endpoint: WorkspaceMemberEndpoint.MEMBER,
      formActionType: FormActionType.Create,
      modalName: ServiceModalName.FormModal,
      label: {
        title: tModal('addNewMember'),
        description: tDescription('addUserToWorkspaceByEmailAddress'),
        confirmButton: tButton('sendInvitation'),
      },
      inputList: [
        {
          name: 'email',
          defaultValue: '',
          inputType: InputType.Text,
          placeholder: tForm('enterTheEmailYouWantToInvite'),
          label: tForm('members'),
          required: true,
        },
      ],
      validationSchema: schema,
    },
  };

  return (WORKSPACE_FORM[endpoint] ?? WORKSPACE_FORM[ApiEndpoint.DEFAULT]) as FormConfig;
};
